import {
  VaultFile,
  VaultFileKind,
  VaultFileVisibility
} from "@wingspanhq/wingspan-api-sdk";
import { vaultFilesApi } from "@api";
import { useWSMutation } from "../../query/helpers";
import { toLocalVaultUploadUrl } from "./toLocalVaultUploadUrl";

export type UploadVaultFileParams = {
  file: File;
  kind?: VaultFileKind;
  visibility?: VaultFileVisibility;
};

/**
 * The v3 replacement for a v1 member-files multipart upload: presign
 * (`createVaultFileUpload`) → PUT the bytes at the returned storage URL →
 * register the vault file (`createVaultFile` with the presigned `uploadId`).
 * Returns the registered `VaultFile`, ready to bind to whatever resource
 * needs it.
 *
 * Not a registry entry — the registry is 1:1 with the SDK, and this
 * orchestrates two SDK calls around a byte PUT that is not an SDK call at all.
 */
export const useMutationUploadVaultFile = () =>
  useWSMutation<VaultFile, unknown, UploadVaultFileParams>(
    async ({
      file,
      kind = VaultFileKind.GENERIC,
      visibility = VaultFileVisibility.PRIVATE
    }) => {
      const upload = await vaultFilesApi.createVaultFileUpload({
        vaultFileUploadCreate: {
          filename: file.name,
          mimeType: file.type,
          contentLength: file.size
        }
      });

      const uploadResponse = await fetch(
        toLocalVaultUploadUrl(upload.uploadUrl),
        {
          method: "PUT",
          headers: upload.requiredHeaders,
          body: file
        }
      );

      if (!uploadResponse.ok) {
        throw new Error(
          `Failed to upload file (${uploadResponse.status} ${uploadResponse.statusText})`
        );
      }

      const vaultFile = await vaultFilesApi.createVaultFile({
        vaultFileCreate: {
          uploadId: upload.uploadId,
          title: file.name,
          filename: file.name,
          mimeType: file.type,
          kind,
          visibility
        }
      });

      if (!vaultFile.id) {
        throw new Error("Uploaded file was registered without an id");
      }

      return vaultFile;
    }
  );
